// Hands-free re-entry for people who already sign in through central identity.
//
// THE PROBLEM. Three sessions sit behind this portal: the portal's own, the
// l2u-identity session, and Okta's. When the portal session lapses but the two
// behind it are alive, the user still has to find and press "Sign in with SSO"
// -- a click whose only outcome is an invisible round trip that signs them
// straight back in. This records that a person is an SSO user so the next visit
// can start that round trip without asking.
//
// WHY NOT prompt=none. The OIDC-native answer is a silent probe: redirect with
// prompt=none and get error=login_required back if there is no session. Probed
// against this project's identity server on 2026-09-05 and it is IGNORED --
// prompt=none and a control request both 302 to the login UI, and the discovery
// document advertises no prompt_values_supported. A probe would therefore strand
// this portal's password users on an identity login page with no route back to their
// password form, so the decision is made from a local hint instead.
//
// THE SAFETY PROPERTY. Only a completed SSO callback records the hint. A
// password user never completes one, so never carries it, so is never redirected
// away from the form they need. Nothing here is a security boundary -- the hint
// only decides whether to START a redirect, and every real check still happens
// at identity, at the callback, and at /auth/whoami.

/** localStorage: "this person signs in through central identity". Survives tabs. */
export const SSO_HINT_KEY = 'l2u.sso.hint';

/**
 * sessionStorage: "this tab already tried". Deliberately NOT localStorage --
 * per-tab and dying with the tab is what makes it a loop breaker rather than a
 * permanent opt-out. A later genuine visit still gets the convenience.
 */
export const SSO_ATTEMPT_KEY = 'l2u.sso.attempted';

/**
 * The query parameter that suppresses the redirect and clears the hint.
 *
 * Deliberately NOT `sso`: cs-console already uses `?sso=<code>` to report SSO
 * failures (cookie, expired, denied, upstream, noaccess), so an opt-out on that
 * name would arrive looking like an unrecognised notice code. Keeping the two
 * meanings on separate parameters is what lets all three portals share one
 * escape hatch.
 */
export const SSO_OFF_PARAM = 'sso_auto';
const SSO_OFF_VALUE = 'off';

/**
 * Parameters that mean an SSO attempt already failed. `error`/`error_description`
 * are the OIDC-standard pair; `sso` is cs-console's own notice channel, whose
 * worker redirects back with a code rather than an OAuth error. Restarting on
 * any of them retries the failure that just happened and buries its explanation.
 */
const FAILURE_PARAMS = ['error', 'error_description', 'sso'];

/**
 * Failures that are only a failure at a particular VALUE, so an unrelated use of
 * the same parameter name does not disable the feature for everyone.
 */
const FAILURE_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['auth', 'error'],
  // The page a session TIMEOUT just landed on. Two reasons to refuse here, and
  // the first is the serious one:
  //
  //   1. A timeout navigates to the login page AND starts a central-logout
  //      navigation. Since the hint deliberately survives a timeout, auto-start
  //      would race that logout -- and if it won, the identity session would
  //      never end and stale Okta claims would live on. That is the exact bug
  //      the logout hand-off was added to fix.
  //   2. Someone timed out should be told why. Redirecting past "your session
  //      reached its 12-hour limit" hides the only explanation.
  //
  // The convenience is not lost, only deferred: the notice lives on this URL
  // alone, so the user's NEXT visit has a clean one and starts hands-free.
  //
  // 'idle' is kept although nothing emits it any more -- the client-side
  // inactivity timeout that produced it has been removed. A browser still
  // running the bundle that HAD that timer keeps its 1-hour deadline for as
  // long as the tab lives, and reason 1 above is exactly what would go wrong
  // if such a tab timed out once this list had stopped recognising it.
  ['reason', 'idle'],
  ['reason', 'expired'],
];

/**
 * Checked in the fragment as well as the query, because Supabase reports an
 * expired or already-used link there: `#error=access_denied&error_code=otp_expired`.
 * Query-only would send someone holding a dead invite link off to SSO and lose
 * the message telling them to ask for a new one.
 */
function carriesFailure(search: string, hash: string): boolean {
  const inQuery = new URLSearchParams(search);
  const inFragment = new URLSearchParams(hash.replace(/^#/, ''));
  if (FAILURE_PARAMS.some((p) => inQuery.has(p) || inFragment.has(p))) return true;
  return FAILURE_PAIRS.some(([k, v]) => inQuery.get(k) === v || inFragment.get(k) === v);
}

// Structural rather than lib.dom's Storage so tests pass a plain object, and so
// a null (SSR, or a browser refusing storage entirely) is expressible.
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Resolves a storage the caller cannot safely touch directly.
 *
 * `window.localStorage` is not merely a property: in Chrome with site data
 * blocked, and in Firefox with dom.storage.enabled=false, the GETTER ITSELF
 * throws a SecurityError. Wrapping only the read/write methods therefore missed
 * the actual failure -- the throw happened at the call site, inside a login-page
 * effect, uncaught, unmounting the tree and leaving a blank screen for a
 * password user who could otherwise have typed a password.
 *
 * Takes a thunk rather than a value so the access happens INSIDE the try. Also
 * folds away the `typeof window === 'undefined'` guard every call site repeated.
 */
export function safeStorage(get: () => StorageLike | null | undefined): StorageLike | null {
  try {
    return get() ?? null;
  } catch {
    return null;
  }
}

// Every accessor is wrapped. Reading localStorage THROWS rather than returning
// null when a browser is set to block site data, and an exception on a login
// page mount would blank the screen for a user who could otherwise have typed a
// password. Unavailable storage degrades to "no hint", which is the pre-feature
// behaviour: the button shows and nothing auto-starts.
function read(storage: StorageLike | null, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function write(storage: StorageLike | null, key: string, value: string): void {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Nothing to do and nothing to report: the next visit simply shows a button.
  }
}

function remove(storage: StorageLike | null, key: string): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // As above.
  }
}

/**
 * Records that this browser belongs to an SSO user. Call ONLY on a completed SSO
 * sign-in.
 *
 * Also clears the tab's attempt marker, given one. A completed callback proves
 * the round trip worked, so the loop breaker has nothing left to break -- and
 * leaving it set made the convenience fire only every OTHER lapse in a
 * long-lived tab. Safe because this runs only past the entitlement check: a
 * sign-in that got a session but was refused never reaches here, so a genuinely
 * looping case keeps its marker.
 */
export function rememberSsoUser(
  storage: StorageLike | null,
  tabStorage?: StorageLike | null,
): void {
  write(storage, SSO_HINT_KEY, '1');
  if (tabStorage) remove(tabStorage, SSO_ATTEMPT_KEY);
}

/**
 * Forgets the hint. Call on sign-out.
 *
 * Load-bearing, not tidiness. signOut already ends the l2u-identity session, but
 * Okta's survives and all three FedEx clients have auto_sso enabled -- so a
 * surviving hint would send the user portal -> identity -> Okta -> straight back
 * in, and the sign-out button would look broken.
 */
export function forgetSsoUser(storage: StorageLike | null): void {
  remove(storage, SSO_HINT_KEY);
}

export function hasSsoHint(storage: StorageLike | null): boolean {
  return read(storage, SSO_HINT_KEY) !== null;
}

/**
 * Marks this tab as "do not auto-start right now". Set BEFORE navigating.
 *
 * Two duties. It breaks a redirect loop when identity bounces back with neither
 * a session nor an error; and an automatic sign-out sets it so the login page a
 * timeout lands on does not start SSO into the central-logout navigation that is
 * already in flight. Cleared by a completed sign-in.
 */
export function markSsoAttempted(storage: StorageLike | null): void {
  write(storage, SSO_ATTEMPT_KEY, '1');
}

export function hasAttemptedSso(storage: StorageLike | null): boolean {
  return read(storage, SSO_ATTEMPT_KEY) !== null;
}

/**
 * True when the URL asks to stay on the password form.
 *
 * Parsed rather than substring-matched so a different parameter that merely
 * contains the text cannot disable SSO by accident.
 */
export function wantsSsoOff(search: string): boolean {
  return new URLSearchParams(search).get(SSO_OFF_PARAM) === SSO_OFF_VALUE;
}

// Parameters that mean the URL is carrying a credential to redeem: a magic
// link, an invite, or a password recovery. Supabase falls back to its Site URL
// when a redirectTo is not allow-listed, so these land on the LOGIN page, which
// forwards them on to the page that redeems them.
//
// Auto-starting SSO on one of these replaces the invite with a redirect and
// strands someone who has no password yet -- so it is refused on sight rather
// than by ordering this check against whichever effect does the forwarding.
const CREDENTIAL_PARAMS = ['code', 'token_hash', 'access_token', 'refresh_token'];

function carriesCredential(search: string, hash: string): boolean {
  const inQuery = new URLSearchParams(search);
  const inFragment = new URLSearchParams(hash.replace(/^#/, ''));
  return CREDENTIAL_PARAMS.some((p) => inQuery.has(p) || inFragment.has(p));
}

/**
 * Whether the login page should start central sign-in instead of rendering.
 *
 * Pure: every signal is passed in, so the decision is testable without a DOM,
 * a router or a Supabase client. The refusal guards are the interesting part --
 * see the tests, which name what each one prevents.
 */
export function shouldAutoStartSso(input: {
  ssoConfigured: boolean;
  hasHint: boolean;
  alreadyAttempted: boolean;
  search: string;
  hash?: string;
}): boolean {
  if (!input.ssoConfigured) return false;
  if (!input.hasHint) return false;
  if (input.alreadyAttempted) return false;
  if (wantsSsoOff(input.search)) return false;
  if (carriesCredential(input.search, input.hash ?? '')) return false;

  // A failure already rode back. Redirecting again reproduces it and hides the
  // message that explains what went wrong.
  if (carriesFailure(input.search, input.hash ?? '')) return false;

  return true;
}

/**
 * The login page's whole decision, side effects included: reads the signals,
 * applies the guards, and returns whether the caller should start central
 * sign-in now.
 *
 * Exists so no portal's login component carries this logic. Two of the three
 * consuming components have no test harness at all -- one of them would need
 * six mocks including a shared UI package -- so logic left in them would ship
 * unverified. Here it is covered identically for every portal without a DOM.
 *
 * Both storages are passed in rather than reached for, which is what makes the
 * "hostile storage" and "no storage" paths testable.
 *
 * `search` must be captured during RENDER, not read inside the effect: at least
 * one portal strips auth errors from the URL in an effect of its own, and if
 * that runs first the error guard below would see a cleaned URL and redirect
 * into the failure it is meant to prevent.
 */
export function beginSsoAutoStart(input: {
  ssoConfigured: boolean;
  search: string;
  hash?: string;
  local: StorageLike | null;
  session: StorageLike | null;
}): boolean {
  // Checked before anything else, and honoured even when SSO is unconfigured:
  // the opt-out is a one-way door, so a user who chose the password form is not
  // redirected again on a later visit.
  if (wantsSsoOff(input.search)) {
    forgetSsoUser(input.local);
    return false;
  }

  const start = shouldAutoStartSso({
    ssoConfigured: input.ssoConfigured,
    hasHint: hasSsoHint(input.local),
    alreadyAttempted: hasAttemptedSso(input.session),
    search: input.search,
    // Normalised rather than forwarded as-is: one consuming repo builds with
    // exactOptionalPropertyTypes, where `string | undefined` is not assignable
    // to an optional `string`.
    hash: input.hash ?? '',
  });
  if (!start) return false;

  // Marked BEFORE the caller navigates, never after. If identity bounces back
  // with neither a session nor an error, this mark is the only thing standing
  // between the user and an unbreakable redirect loop.
  markSsoAttempted(input.session);
  return true;
}

/**
 * The query parameter a portal's own backend sets on a successful SSO return.
 *
 * Only cs-console needs it: its browser is token-free and its worker mints the
 * SAME cookie for an SSO sign-in as for a password one, deliberately, so the
 * client has no other way to learn which method was used. Billing and compliance
 * see the completed callback in their own component and never set this.
 */
export const SSO_SUCCESS_PARAM = 'sso_ok';

/**
 * Whether the current URL marks a completed SSO return.
 *
 * PURE -- it writes nothing, and that is the point. An earlier version recorded
 * the hint on sight, which let a THIRD PARTY arm it: a link to `/?sso_ok=1` sent
 * to anyone wrote the flag before the session was known, so a console PASSWORD
 * user could be armed by a stranger and then redirected to an identity login
 * page that cannot authenticate them, with no route back to their own form.
 *
 * The caller must therefore corroborate the marker against something the URL
 * cannot forge -- an authenticated `/api/auth/me` that comes back with a user --
 * before calling rememberSsoUser. The marker still has to be stripped from the
 * address bar either way: left in place, a refresh would re-arm the hint after
 * the user had opted out.
 */
export function hasSsoSuccessMarker(search: string): boolean {
  return new URLSearchParams(search).has(SSO_SUCCESS_PARAM);
}
